import { useState, useEffect, useCallback, useRef } from "react";
import { ThemeProvider, useTheme } from "./ThemeContext";
import { STATUS } from "./theme";
import { get, put } from "./api";
import T from "./i18n";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Pedidos from "./pages/Pedidos";
import Entregadores from "./pages/Entregadores";
import Relatorios from "./pages/Relatorios";
import DevoEntregadores from "./pages/DevoEntregadores";
import Cadastro from "./pages/Cadastro";
import Configuracao from "./pages/Configuracao";
import BotWhatsApp from "./pages/BotWhatsApp";
import PDV from "./pages/PDV";
import Cambio from "./pages/Cambio";
import Moedas from "./pages/Moedas";
import ImportarProdutos from "./pages/ImportarProdutos";

const API_URL = "https://mixupbr.com.br/api";
const STORAGE_KEY = "mixup_loja_auth";

// Versão do painel — bump a cada atualização publicada
const APP_VERSION = "1.0.0";

// ─── Comparador de versões (semver simples) ─────────────────────────────────────
function versaoMaior(a, b) {
  // retorna true se a > b (ex: "1.2.0" > "1.1.9")
  const pa = String(a || "0").split(".").map(n => parseInt(n, 10) || 0);
  const pb = String(b || "0").split(".").map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] || 0, y = pb[i] || 0;
    if (x > y) return true;
    if (x < y) return false;
  }
  return false;
}

// ─── Guard de Manutenção ────────────────────────────────────────────────────────
// Lê config_app/?chave=manutencao. Se geral OU lojista = true, bloqueia o painel
// (e desloga). Funciona logado ou não. Polling a cada 30s.
function ManutencaoGuard({ children, onBloqueio }) {
  const { theme: C } = useTheme();
  const [bloqueio, setBloqueio] = useState(null); // null = liberado | {mensagem}

  useEffect(() => {
    let ativo = true;
    const checar = async () => {
      try {
        const r = await fetch(`${API_URL}/config_app/?chave=manutencao`);
        const d = await r.json();
        const raw = d?.valor ?? d?.dados;
        if (!raw) { if (ativo) setBloqueio(null); return; }
        const m = typeof raw === "string" ? JSON.parse(raw) : raw;
        const ligado = m && (m.geral === true || m.lojista === true);
        if (!ativo) return;
        if (ligado) {
          setBloqueio({ mensagem: m.mensagem || "Sistema em manutenção. Voltamos em breve!" });
          if (onBloqueio) onBloqueio(); // desloga
        } else {
          setBloqueio(null);
        }
      } catch { /* silencioso — falha de rede não bloqueia */ }
    };
    checar();
    const i = setInterval(checar, 30000);
    return () => { ativo = false; clearInterval(i); };
  }, [onBloqueio]);

  if (bloqueio) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 420, background: C.card, borderRadius: 20, padding: 40, border: `1px solid ${C.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🔧</div>
          <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 10, color: C.text }}>Em Manutenção</div>
          <div style={{ fontSize: 14, color: C.textSoft, lineHeight: 1.5 }}>{bloqueio.mensagem}</div>
        </div>
      </div>
    );
  }
  return children;
}

// ─── Aviso de Atualização (banner fechável) ─────────────────────────────────────
// Lê config_app/?chave=versao_app: { versao, mensagem, lojista:true/false }.
// Se versao > APP_VERSION e setor lojista ativo, mostra banner. Fechável por sessão.
function AvisoAtualizacao() {
  const { theme: C } = useTheme();
  const [aviso, setAviso] = useState(null);
  const [fechado, setFechado] = useState(false);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/config_app/?chave=versao_app`);
        const d = await r.json();
        const raw = d?.valor ?? d?.dados;
        if (!raw) return;
        const v = typeof raw === "string" ? JSON.parse(raw) : raw;
        // só mostra se o setor lojista estiver marcado (ou geral) e a versão for maior
        const setorAtivo = v && (v.geral === true || v.lojista === true);
        if (ativo && setorAtivo && versaoMaior(v.versao, APP_VERSION)) {
          setAviso({ versao: v.versao, mensagem: v.mensagem || "Uma nova versão está disponível." });
        }
      } catch {}
    })();
    return () => { ativo = false; };
  }, []);

  if (!aviso || fechado) return null;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: C.green, color: "#fff", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, fontSize: 13, fontWeight: 600, boxShadow: "0 2px 12px rgba(0,0,0,.25)" }}>
      <span>🎉 {aviso.mensagem} {aviso.versao ? `(v${aviso.versao})` : ""} — recarregue a página para atualizar.</span>
      <button onClick={() => { try { if (typeof window !== "undefined") window.location.reload(true); } catch {} }}
        style={{ padding: "5px 12px", borderRadius: 7, border: "none", background: "#fff", color: C.green, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
        Atualizar agora
      </button>
      <button onClick={() => setFechado(true)}
        style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid rgba(255,255,255,.5)", background: "transparent", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
        ✕
      </button>
    </div>
  );
}

// ─── Tela de Login ────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const { theme: C } = useTheme();
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);
  // 2FA
  const [etapa, setEtapa] = useState("login"); // login | codigo
  const [codigo, setCodigo] = useState("");
  const [emailMasc, setEmailMasc] = useState("");

  const fazer = async () => {
    if (!login.trim() || !senha.trim()) { setErro("Preencha login e senha."); return; }
    setCarregando(true); setErro("");
    try {
      const r = await fetch(`${API_URL}/auth/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tipo: "lojista", email: login.trim(), senha, acao: "login" }),
      });
      const d = await r.json();
      if (d.error === "confirmar_login") {
        setEmailMasc(d.email_mascarado || "");
        setEtapa("codigo");
        setCarregando(false);
        return;
      }
      if (!r.ok || d.error || !d.sucesso) { setErro(d.error || d.erro || "Credenciais inválidas."); setCarregando(false); return; }
      if (d.dados?.token) localStorage.setItem("mixup_loja_token", d.dados.token);
      onLogin({ id: d.dados.loja_id || d.dados.id, nome: d.dados.nome, login: d.dados.login || login.trim() });
    } catch { setErro("Erro de conexão. Tente novamente."); }
    setCarregando(false);
  };

  const confirmar = async () => {
    if (!/^\d{6}$/.test(codigo)) { setErro("Digite o código de 6 dígitos."); return; }
    setCarregando(true); setErro("");
    try {
      const r = await fetch(`${API_URL}/auth/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tipo: "lojista", email: login.trim(), senha, codigo, acao: "confirmar_login_loja" }),
      });
      const d = await r.json();
      if (!r.ok || d.error || !d.sucesso) { setErro(d.error || d.erro || "Código inválido."); setCarregando(false); return; }
      if (d.dados?.token) localStorage.setItem("mixup_loja_token", d.dados.token);
      onLogin({ id: d.dados.loja_id || d.dados.id, nome: d.dados.nome, login: d.dados.login || login.trim() });
    } catch { setErro("Erro de conexão. Tente novamente."); }
    setCarregando(false);
  };

  const reenviar = () => { setCodigo(""); setErro(""); fazer(); };

  const inp = { width: "100%", padding: "11px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 380, background: C.card, borderRadius: 20, padding: 36, border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontWeight: 800, fontSize: 28 }}>
            <span style={{ color: C.red }}>Mix</span><span style={{ color: C.green }}>Up</span>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 600, letterSpacing: "0.08em", marginTop: 4, textTransform: "uppercase" }}>Painel do Lojista</div>
        </div>

        {etapa === "login" ? (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>Login</label>
              <input style={inp} value={login} onChange={e => setLogin(e.target.value)}
                onKeyDown={e => e.key === "Enter" && fazer()}
                placeholder="usuario_da_loja" autoCapitalize="none" autoComplete="username" />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>Senha</label>
              <input style={inp} type="password" value={senha} onChange={e => setSenha(e.target.value)}
                onKeyDown={e => e.key === "Enter" && fazer()}
                placeholder="••••••••" autoComplete="current-password" />
            </div>

            {erro && <div style={{ fontSize: 13, color: C.red, fontWeight: 600, marginBottom: 14, textAlign: "center" }}>{erro}</div>}

            <button onClick={fazer} disabled={carregando}
              style={{ width: "100%", padding: 14, borderRadius: 10, border: "none", background: C.red, color: "#fff", fontWeight: 700, fontSize: 15, cursor: carregando ? "wait" : "pointer", opacity: carregando ? 0.7 : 1 }}>
              {carregando ? "Entrando..." : "Entrar"}
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 18, textAlign: "center", lineHeight: 1.5 }}>
              Enviamos um código de 6 dígitos para o e-mail{emailMasc ? " " + emailMasc : " da loja"}. Digite-o para entrar.
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>Código</label>
              <input style={{ ...inp, textAlign: "center", letterSpacing: "10px", fontSize: 24, fontWeight: 700 }}
                value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={e => e.key === "Enter" && confirmar()}
                placeholder="000000" inputMode="numeric" maxLength={6} autoFocus />
            </div>

            {erro && <div style={{ fontSize: 13, color: C.red, fontWeight: 600, marginBottom: 14, textAlign: "center" }}>{erro}</div>}

            <button onClick={confirmar} disabled={carregando}
              style={{ width: "100%", padding: 14, borderRadius: 10, border: "none", background: C.red, color: "#fff", fontWeight: 700, fontSize: 15, cursor: carregando ? "wait" : "pointer", opacity: carregando ? 0.7 : 1 }}>
              {carregando ? "Verificando..." : "Confirmar"}
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
              <span onClick={() => { setEtapa("login"); setCodigo(""); setErro(""); }} style={{ fontSize: 13, color: C.textMuted, cursor: "pointer" }}>← Voltar</span>
              <span onClick={reenviar} style={{ fontSize: 13, color: C.red, fontWeight: 600, cursor: "pointer" }}>Reenviar código</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── App principal ────────────────────────────────────────────────────────────
function AppInner() {
  const { theme: C } = useTheme();

  const [loja, setLoja] = useState(() => {
    try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
  });

  const handleLogin = (dadosLoja) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dadosLoja));
    setLoja(dadosLoja);
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem("mixup_loja_token");
    setLoja(null);
  };

  if (!loja) return <LoginScreen onLogin={handleLogin} />;

  const LOJA_ID = loja.id;

  return <AppLogado C={C} LOJA_ID={LOJA_ID} loja={loja} onLogout={handleLogout} />;
}

// ─── App logado ───────────────────────────────────────────────────────────────
function AppLogado({ C, LOJA_ID, loja, onLogout }) {
  const [page, setPage] = useState("dashboard");
  const [cadSub, setCadSub] = useState("categorias");
  const [cfgSub, setCfgSub] = useState("configuracao_tela");
  const [lang, setLang] = useState(() => localStorage.getItem("mixup_lang") || "pt");
  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // Logo da loja (busca via API)
  const [logoUrl, setLogoUrl] = useState("");

  const [chatTotalNaoLidos, setChatTotalNaoLidos] = useState(0);
  const chatPrevTotalRef = useRef(0);
  const chatSomRef = useRef(null);

  const playChatSound = () => {
    try {
      if (!chatSomRef.current) chatSomRef.current = new Audio("/mixup-lojista/notification.mp3");
      chatSomRef.current.currentTime = 0;
      chatSomRef.current.play().catch(() => {});
    } catch {}
  };

  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch(`https://mixupbr.com.br/api/chat/?tipo=loja&id=${LOJA_ID}`);
        const d = await r.json();
        if (d.ok) {
          const total = (d.conversas || []).reduce((sum, c) => sum + (parseInt(c.nao_lidos) || 0), 0);
          setChatTotalNaoLidos(total);
          if (total > chatPrevTotalRef.current && chatPrevTotalRef.current >= 0) playChatSound();
          chatPrevTotalRef.current = total;
        }
      } catch {}
    };
    poll();
    const i = setInterval(poll, 5000);
    return () => clearInterval(i);
  }, [LOJA_ID]);

  // Buscar logo_url da loja
  useEffect(() => {
    get("lojas", { id: LOJA_ID }).then(function(res) {
      if (res.dados && res.dados.length > 0) {
        setLogoUrl(res.dados[0].logo_url || "");
      }
    }).catch(function() {});
  }, [LOJA_ID]);

  // ── HEARTBEAT (presença real do lojista) ──
  // Pinga POST /api/heartbeat a cada 60s.
  // Backend grava last_heartbeat = NOW(). Cliente vê loja como aberta apenas
  // se o último ping foi nos últimos ~2 minutos.
  //
  // COMPORTAMENTO POR AMBIENTE:
  // • Electron PDV (window.__ELECTRON_PDV__): SEMPRE pinga. Não pausa quando
  //   minimizado/atrás de outras janelas. Lojista usa WhatsApp/outras telas
  //   sem perder pedidos. Só para se fechar o programa ou clicar Offline.
  // • Navegador comum (Chrome/Edge web): pausa quando aba oculta pra não
  //   ficar online indevidamente quando lojista esquece a aba aberta.
  useEffect(() => {
    if (!LOJA_ID) return;

    const isElectronPDV = typeof window !== "undefined" && !!window.__ELECTRON_PDV__;
    let intervalo = null;

    const enviarHeartbeat = async () => {
      try {
        const token = localStorage.getItem("mixup_loja_token");
        if (!token) return;
        await fetch("https://mixupbr.com.br/api/heartbeat/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + token,
          },
          body: JSON.stringify({}),
        });
      } catch (e) {
        // silencioso — heartbeat não pode quebrar fluxo do painel
      }
    };

    const iniciar = () => {
      if (intervalo) return;
      enviarHeartbeat(); // dispara imediatamente
      intervalo = setInterval(enviarHeartbeat, 60000); // a cada 60s
    };

    const parar = () => {
      if (intervalo) {
        clearInterval(intervalo);
        intervalo = null;
      }
    };

    // ── ELECTRON: sempre liga, nunca pausa ──
    if (isElectronPDV) {
      iniciar();
      return () => parar();
    }

    // ── NAVEGADOR COMUM: respeita visibilidade da aba ──
    if (typeof document !== "undefined" && !document.hidden) iniciar();

    const onVisibilityChange = () => {
      if (document.hidden) {
        parar();
      } else {
        iniciar();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      parar();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [LOJA_ID]);

  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 2800); };

  const fetchOrders = useCallback(async () => {
    try {
      const res = await get("pedidos", { loja_id: LOJA_ID, limite: 500 });
      if (res.dados) setOrders(res.dados);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [LOJA_ID]);

  useEffect(() => { fetchOrders(); const i = setInterval(fetchOrders, 10000); return () => clearInterval(i); }, [fetchOrders]);
  useEffect(() => { const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { localStorage.setItem("mixup_lang", lang); }, [lang]);

  const advanceOrder = async (id) => {
    const o = orders.find(x => x.id === id);
    if (!o) return;
    const cfg = STATUS[o.status];
    if (!cfg?.ns) return;
    try {
      await put("pedidos", id, { status: cfg.ns });
      notify("Pedido atualizado!");
      fetchOrders();
    } catch { notify("Erro ao atualizar", false); }
  };

  const rejectOrder = async (id) => {
    try {
      await put("pedidos", id, { status: "cancelado", motivo_cancelamento: "Recusado pelo lojista" });
      notify("Pedido cancelado", false);
      fetchOrders();
    } catch { notify("Erro ao cancelar", false); }
  };

  const novos = orders.filter(o => o.status === "novo").length;
  const collapsed = page === "pedidos" || page === "pdv";
  const _ = (k) => T(lang, k);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, fontFamily: "Segoe UI, system-ui, sans-serif", color: C.text, transition: "background .3s, color .3s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}body{background:${C.bg}}
        ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes glow{0%,100%{box-shadow:0 0 6px ${C.red}40}50%{box-shadow:0 0 18px ${C.red}80}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .nav-item{transition:all .2s;cursor:pointer;border-radius:10px}.nav-item:hover{background:rgba(128,128,128,.08)}
        .sub-g{transition:all .15s}.sub-g:hover{background:rgba(128,128,128,.07)}
        .sub-s{transition:all .15s}.sub-s:hover{background:rgba(128,128,128,.06)!important}
        .stat-card{transition:all .3s}.stat-card:hover{transform:translateY(-4px);box-shadow:0 10px 30px rgba(0,0,0,.2)!important}
        .order-card{transition:all .2s;cursor:pointer}.order-card:hover{background:${C.cardHover}!important;transform:translateY(-1px)}
        .act-btn{transition:all .15s;cursor:pointer}.act-btn:hover{transform:translateY(-1px);filter:brightness(1.1)}.act-btn:active{transform:scale(.97)}
        .rej-btn{transition:all .15s;cursor:pointer}.rej-btn:hover{background:${C.red}20!important;color:${C.red}!important}
        .form-btn{transition:all .15s;cursor:pointer}.form-btn:hover{filter:brightness(1.1);transform:translateY(-1px)}
        input:focus,select:focus{border-color:${C.red}!important}
      `}</style>

      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, background: toast.ok ? C.green : C.red, color: "#fff", padding: "12px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14, boxShadow: "0 8px 30px rgba(0,0,0,.4)", animation: "slideDown .3s ease-out" }}>{toast.msg}</div>
      )}

      <Sidebar
        page={page} setPage={setPage}
        collapsed={collapsed} novos={novos} clock={clock} lang={lang}
        nomeLoja={loja.nome}
        onLogout={onLogout}
        lojaId={LOJA_ID}
        logoUrl={logoUrl}
      />

      <div style={{ flex: 1, padding: (page === "pedidos" || page === "pdv") ? 16 : 24, overflowY: "auto", minHeight: "100vh" }}>
        {loading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 40, height: 40, border: "3px solid " + C.red, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 16px" }} />
              <div style={{ color: C.textSoft, fontWeight: 600 }}>Carregando...</div>
            </div>
          </div>
        )}

        {!loading && page === "dashboard" && <Dashboard orders={orders} clock={clock} goToPedidos={() => setPage("pedidos")} lang={lang} />}
        {!loading && page === "pedidos" && <Pedidos orders={orders} onAdvance={advanceOrder} onReject={rejectOrder} onRefresh={fetchOrders} lang={lang} lojaId={LOJA_ID} goTo={setPage} />}
        {!loading && page === "pdv" && <PDV orders={orders} onAdvance={advanceOrder} onRefresh={fetchOrders} lang={lang} lojaId={LOJA_ID} goTo={setPage} />}
        {!loading && page === "cambio" && <Cambio lojaId={LOJA_ID} />}
        {!loading && page === "moedas" && <Moedas lojaId={LOJA_ID} />}
        {!loading && page === "entregadores" && <Entregadores lojaId={LOJA_ID} lang={lang} />}

        {!loading && page === "relatorios" && <Relatorios orders={orders} lang={lang} lojaId={LOJA_ID} />}
        {!loading && page === "devo_entregadores" && <DevoEntregadores lojaId={LOJA_ID} lang={lang} />}

        {!loading && page === "cadastro" && (
          <div style={{ animation: "fadeUp .4s ease-out" }}>
            {/* Tabs cadastro */}
            <div style={{ display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap" }}>
              {[
                { key: "categorias", label: _("categorias") }, { key: "subcategorias", label: _("subCategorias") },
                { key: "produtos", label: _("produtos") }, { key: "importar", label: "📦 Importar" },
                { key: "caixinha", label: _("caixinhaSurpresa") }, { key: "promocoes", label: "Promoções" },
                { key: "catalogo", label: "🌐 Catálogo Web" },
              ].map(function(s) {
                var active = cadSub === s.key;
                return (
                  <button key={s.key} onClick={function() { setCadSub(s.key); }}
                    style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid " + (active ? C.red : C.border), background: active ? C.red + "15" : C.card, color: active ? C.red : C.textSoft, fontWeight: 700, fontSize: 12, cursor: "pointer", transition: "all .15s", fontFamily: "inherit" }}>
                    {s.label}
                  </button>
                );
              })}
            </div>
            {cadSub === "importar" 
              ? <ImportarProdutos lojaId={LOJA_ID} onImported={fetchOrders} />
              : <Cadastro sub={cadSub} lojaId={LOJA_ID} lang={lang} />
            }
          </div>
        )}

        {!loading && page === "config" && (
          <div style={{ animation: "fadeUp .4s ease-out" }}>
            {/* Tabs configuração */}
            <div style={{ display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap" }}>
              {[
                { key: "configuracao_tela", label: "Tela" }, { key: "horarios", label: _("horarios") },
                { key: "dados_conta", label: _("dadosConta") }, { key: "api_pagamento", label: _("apiPagamento") },
                { key: "impressora", label: _("impressora") }, { key: "tipos_pagamento", label: _("tiposPagamento") },
                { key: "idiomas", label: _("idiomas") },
              ].map(function(s) {
                var active = cfgSub === s.key;
                return (
                  <button key={s.key} onClick={function() { setCfgSub(s.key); }}
                    style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid " + (active ? C.red : C.border), background: active ? C.red + "15" : C.card, color: active ? C.red : C.textSoft, fontWeight: 700, fontSize: 12, cursor: "pointer", transition: "all .15s", fontFamily: "inherit" }}>
                    {s.label}
                  </button>
                );
              })}
            </div>
            <Configuracao sub={cfgSub} lojaId={LOJA_ID} lang={lang} setLang={setLang} />
          </div>
        )}
        {!loading && page === "bot"     && <BotWhatsApp lojaId={LOJA_ID} nomeLoja={loja.nome} />}
      </div>
    </div>
  );
}

function limparSessaoLojista() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem("mixup_loja_token");
  } catch {}
}

export default function App() {
  return (
    <ThemeProvider>
      <ManutencaoGuard onBloqueio={limparSessaoLojista}>
        <AvisoAtualizacao />
        <AppInner />
      </ManutencaoGuard>
    </ThemeProvider>
  );
}
